import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { GenericEventFeed } from './GenericEventFeed';
import { useResolvedSource, useSourceEvents } from './hooks';
import {
    useAddEventToReportMenuItem,
    useOpenInNewWindow,
    useWebSocketStatus,
} from '@/hooks';
import { useEventFeedStore } from '@/stores/event-feed.store';
import { getDetectionLabel } from '@/utils/detection-classes';
import { SortOption } from '@/utils/query-filters';
import { SortOptionUrl, URL_PARAMS } from '@/utils/query-url-params';
import { SIDEBAR_EVENT_FEED_CLASS_IDS } from '@/constants/sidebar-events-filter';
import { SourceType } from '@/types/source';
import { AppRoutes } from '@/constants/routes';
import type { FeedEmptyStateProps } from './components';
import {
    ANALYZING_REFRESH_INTERVAL_MS,
    LIVE_REFRESH_INTERVAL_MS,
} from './constants';

interface SourceEventsFeedProps {
    sourceId: string;
    pageKey?: string;
    titleContent?: ReactNode;
    emptyState?: FeedEmptyStateProps;
}

export function SourceEventsFeed({
    sourceId,
    pageKey,
    titleContent,
    emptyState,
}: SourceEventsFeedProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const navigate = useNavigate();
    const isConnected = useWebSocketStatus();
    const { openEvent } = useOpenInNewWindow();
    const addToReportItem = useAddEventToReportMenuItem();

    const isLive = useEventFeedStore((state) => state.playerIsLive);
    const sourceFocusedEventId = useEventFeedStore(
        (state) => state.feedPlayingEventId,
    );

    const { resolvedSource, sourceReady, sourceIsAnalyzing } =
        useResolvedSource(sourceId);

    const orderBy = useMemo(() => {
        if (sourceIsAnalyzing) return 'firstFrameTime desc';
        if (resolvedSource?.type === SourceType.FILE)
            return 'firstFrameTime asc';
        return 'firstFrameTime desc';
    }, [sourceIsAnalyzing, resolvedSource?.type]);

    const refreshInterval = useMemo(() => {
        if (!sourceReady) return 0;
        if (sourceIsAnalyzing) return ANALYZING_REFRESH_INTERVAL_MS;
        if (isLive) return LIVE_REFRESH_INTERVAL_MS;
        return 0;
    }, [sourceReady, sourceIsAnalyzing, isLive]);

    const { events, isLoading, isLoadingMore, hasMore, loadMore } =
        useSourceEvents(
            sourceReady ? sourceId : '',
            { orderBy, classId: SIDEBAR_EVENT_FEED_CLASS_IDS },
            refreshInterval,
            pageKey,
        );

    const handleViewAll = () => {
        const sortOption =
            resolvedSource?.type === SourceType.FILE && !sourceIsAnalyzing
                ? SortOption.DATE_OLDEST
                : SortOption.DATE_NEWEST;
        const params = new URLSearchParams();
        params.set('sources', sourceId);
        params.set(URL_PARAMS.SORT, SortOptionUrl[sortOption]);
        navigate(`${AppRoutes.EVENTS}?${params.toString()}`);
    };

    return (
        <GenericEventFeed
            containerTitle={t('sidebar.sourceEvents')}
            titleContent={titleContent}
            isLive={isLive}
            isConnected={isConnected}
            onViewAll={handleViewAll}
            items={events}
            isLoading={!sourceReady || isLoading}
            emptyState={emptyState}
            itemKey={(event) => event.id}
            itemEvent={(event) => event}
            cardTitle={t('page.event')}
            itemDescription={(event) => getDetectionLabel(t, event.classId)}
            itemActionMenuItems={(_, event) => addToReportItem(event)}
            watchingOptions={(_, event) => ({
                isFocused: sourceFocusedEventId === event.id,
            })}
            onNavigate={(event, clickEvent) =>
                openEvent(event.id, clickEvent, {
                    state: {
                        feedContext: {
                            type: 'sourceEvents',
                            sourceId,
                            pageKey,
                        },
                    },
                })
            }
            useRelativeTime={false}
            pagination={{ hasMore, isLoadingMore, onLoadMore: loadMore }}
            quickViewContextType="event-feed"
        />
    );
}
